import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Text,
  Button,
  Icon
} from "@chakra-ui/react";
import { useOutsideClick } from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

/**
 * Componente de busca e seleção de câmera com filtro
 */
export const CameraSearchSelect = ({ 
  cameras = [], 
  value, 
  onChange, 
  placeholder = "Buscar câmera por nome...", 
  size = "md",
  isDisabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCameras, setFilteredCameras] = useState(cameras);
  const ref = useRef();

  // Fechar quando clicar fora
  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  // Filtrar câmeras baseado na busca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCameras(cameras);
    } else {
      const filtered = cameras.filter(camera =>
        camera.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        camera.id.toString().includes(searchTerm)
      );
      setFilteredCameras(filtered);
    }
  }, [searchTerm, cameras]);

  // Obter nome da câmera selecionada
  const getSelectedCameraName = () => {
    if (!value) return "";
    const camera = cameras.find(cam => cam.id.toString() === value.toString());
    return camera ? camera.name : "";
  };

  // Limpar seleção
  const clearSelection = () => {
    onChange({ target: { name: "cameraId", value: "" } });
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <Box position="relative" ref={ref}>
      <InputGroup size={size}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={value ? getSelectedCameraName() : placeholder}
          value={isOpen ? searchTerm : (value ? getSelectedCameraName() : "")}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => !isDisabled && setIsOpen(true)}
          cursor="pointer"
          readOnly={!isOpen}
          isDisabled={isDisabled}
          bg="white"
        />
        {value && !isDisabled && (
          <Button
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            size="xs"
            variant="ghost"
            onClick={clearSelection}
            zIndex={2}
            color="gray.400"
            _hover={{ color: "gray.600" }}
          >
            ✕
          </Button>
        )}
      </InputGroup>

      {isOpen && !isDisabled && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          <List spacing={0}>
            {filteredCameras.length === 0 ? (
              <ListItem p={3} color="gray.500" textAlign="center">
                Nenhuma câmera encontrada
              </ListItem>
            ) : (
              filteredCameras.map((camera) => (
                <ListItem
                  key={camera.id}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: "gray.100" }}
                  borderBottom="1px solid"
                  borderBottomColor="gray.100"
                  onClick={() => {
                    onChange({ target: { name: "cameraId", value: camera.id.toString() } });
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <Text fontWeight="medium">{camera.name}</Text>
                  <Text fontSize="sm" color="gray.500">ID: {camera.id}</Text>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

/**
 * Componente de busca e seleção de usuário com filtro
 */
export const UserSearchSelect = ({ 
  users = [], 
  value, 
  onChange, 
  placeholder = "Buscar usuário...", 
  size = "md",
  isDisabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState(users);
  const ref = useRef();

  // Fechar quando clicar fora
  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  // Filtrar usuários baseado na busca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        user.id.toString().includes(searchTerm)
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Obter nome do usuário selecionado
  const getSelectedUserName = () => {
    if (!value) return "";
    const user = users.find(u => u.id.toString() === value.toString());
    return user ? (user.username || user.name || `Usuário ${user.id}`) : "";
  };

  // Limpar seleção
  const clearSelection = () => {
    onChange({ target: { name: "assigneeId", value: "" } });
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <Box position="relative" ref={ref}>
      <InputGroup size={size}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={value ? getSelectedUserName() : placeholder}
          value={isOpen ? searchTerm : (value ? getSelectedUserName() : "")}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => !isDisabled && setIsOpen(true)}
          cursor="pointer"
          readOnly={!isOpen}
          isDisabled={isDisabled}
          bg="white"
        />
        {value && !isDisabled && (
          <Button
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            size="xs"
            variant="ghost"
            onClick={clearSelection}
            zIndex={2}
            color="gray.400"
            _hover={{ color: "gray.600" }}
          >
            ✕
          </Button>
        )}
      </InputGroup>

      {isOpen && !isDisabled && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          <List spacing={0}>
            {filteredUsers.length === 0 ? (
              <ListItem p={3} color="gray.500" textAlign="center">
                Nenhum usuário encontrado
              </ListItem>
            ) : (
              filteredUsers.map((user) => (
                <ListItem
                  key={user.id}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: "gray.100" }}
                  borderBottom="1px solid"
                  borderBottomColor="gray.100"
                  onClick={() => {
                    onChange({ target: { name: "assigneeId", value: user.id.toString() } });
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <Text fontWeight="medium">{user.username || user.name || `Usuário ${user.id}`}</Text>
                  {user.name && user.username && (
                    <Text fontSize="sm" color="gray.500">Nome: {user.name}</Text>
                  )}
                  <Text fontSize="sm" color="gray.500">ID: {user.id}</Text>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

/**
 * Componente de busca e seleção de algoritmo com filtro
 */
export const AlgorithmSearchSelect = ({ 
  algorithms = [], 
  value, 
  onChange, 
  placeholder = "Buscar algoritmo...", 
  size = "md",
  isDisabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredAlgorithms, setFilteredAlgorithms] = useState(algorithms);
  const ref = useRef();

  // Fechar quando clicar fora
  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  // Filtrar algoritmos baseado na busca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredAlgorithms(algorithms);
    } else {
      const filtered = algorithms.filter(algorithm =>
        (algorithm.name && algorithm.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        algorithm.id.toString().includes(searchTerm)
      );
      setFilteredAlgorithms(filtered);
    }
  }, [searchTerm, algorithms]);

  // Obter nome do algoritmo selecionado
  const getSelectedAlgorithmName = () => {
    if (!value) return "";
    const algorithm = algorithms.find(a => a.id.toString() === value.toString());
    return algorithm ? (algorithm.name || `Algoritmo ${algorithm.id}`) : "";
  };

  // Limpar seleção
  const clearSelection = () => {
    onChange({ target: { name: "algorithmId", value: "" } });
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <Box position="relative" ref={ref}>
      <InputGroup size={size}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={value ? getSelectedAlgorithmName() : placeholder}
          value={isOpen ? searchTerm : (value ? getSelectedAlgorithmName() : "")}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => !isDisabled && setIsOpen(true)}
          cursor="pointer"
          readOnly={!isOpen}
          isDisabled={isDisabled}
          bg="white"
        />
        {value && !isDisabled && (
          <Button
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            size="xs"
            variant="ghost"
            onClick={clearSelection}
            zIndex={2}
            color="gray.400"
            _hover={{ color: "gray.600" }}
          >
            ✕
          </Button>
        )}
      </InputGroup>

      {isOpen && !isDisabled && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="200px"
          overflowY="auto"
          mt={1}
        >
          <List spacing={0}>
            {filteredAlgorithms.length === 0 ? (
              <ListItem p={3} color="gray.500" textAlign="center">
                Nenhum algoritmo encontrado
              </ListItem>
            ) : (
              filteredAlgorithms.map((algorithm) => (
                <ListItem
                  key={algorithm.id}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: "gray.100" }}
                  borderBottom="1px solid"
                  borderBottomColor="gray.100"
                  onClick={() => {
                    onChange({ target: { name: "algorithmId", value: algorithm.id.toString() } });
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <Text fontWeight="medium">{algorithm.name || `Algoritmo ${algorithm.id}`}</Text>
                  <Text fontSize="sm" color="gray.500">ID: {algorithm.id}</Text>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};
